import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import ContentCard from '../components/ContentCard';
import '../styles/editor.css';

const CORE_SECTIONS = [
  {
    type: 'hero',
    title: 'Homepage Banner',
    description: 'The big banner at the top of your homepage — headline, image, and button.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></svg>,
    singleton: true,
    path: 'hero',
  },
  {
    type: 'cta',
    title: 'Call to Action',
    description: 'The bottom banner — a headline, tagline, and button to drive conversions.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    singleton: true,
    path: 'cta',
  },
  {
    type: 'about',
    title: 'About',
    description: 'Your bio, story, and profile photo.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    singleton: true,
  },
  {
    type: 'contact',
    title: 'Contact',
    description: 'Contact info, social links, and contact form.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    singleton: true,
  },
];

const COLLECTION_SECTIONS = [
  {
    type: 'product',
    title: 'Products',
    description: 'Showcase your boards, artwork, or creations.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
    singleton: false,
    path: 'products',
  },
  {
    type: 'gallery',
    title: 'Gallery',
    description: 'Photo gallery with captions.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>,
    singleton: false,
  },
  {
    type: 'post',
    title: 'Blog',
    description: 'News, updates, and stories.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    singleton: false,
    path: 'blog',
  },
  {
    type: 'testimonial',
    title: 'Testimonials',
    description: 'Customer reviews and testimonials.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    singleton: false,
    path: 'testimonials',
  },
  {
    type: 'hours',
    title: 'Opening Hours',
    description: 'Your business opening hours.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    singleton: false,
    path: 'hours',
  },
];

const CONFIG_SECTIONS = [
  {
    type: 'category',
    title: 'Categories',
    description: 'Organize products into browsable categories.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>,
    singleton: false,
    path: 'categories',
  },
  {
    type: 'site',
    title: 'Navigation & Branding',
    description: 'Business name, logo, favicon, tagline, and what you call your offerings.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
    singleton: true,
    path: 'branding',
  },
  {
    type: 'sections',
    title: 'Sections',
    description: 'Show or hide page sections, and customise their headings.',
    icon: <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
    singleton: true,
    path: 'sections',
  },
];

export default function ContentOverview() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading } = useContent(siteId);

  const counts: Record<string, number> = {};
  for (const item of items) {
    const type = item.contentType || item.contentKey.split('#')[0];
    counts[type] = (counts[type] || 0) + 1;
  }

  if (loading) {
    const SkeletonCard = () => (
      <div className="content-card content-card--skeleton">
        <div className="content-card-icon" />
        <div className="content-card-body">
          <div className="skeleton skeleton-title" />
          <div className="skeleton skeleton-desc" />
          <div className="skeleton skeleton-meta" />
        </div>
      </div>
    );
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Content</h1>
            <p className="page-subtitle">Add your story, products, and everything visitors will see.</p>
          </div>
        </div>
        <div className="content-section">
          <div className="content-cards-grid">
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
        </div>
        <div className="content-section">
          <h2 className="content-section-title">Sections</h2>
          <div className="content-cards-grid">
            <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
        </div>
        <div className="content-section">
          <h2 className="content-section-title">Settings</h2>
          <div className="content-cards-grid">
            <SkeletonCard /><SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Content</h1>
          <p className="page-subtitle">Add your story, products, and everything visitors will see.</p>
        </div>
      </div>

      <div className="content-section">
        <div className="content-cards-grid">
          {CORE_SECTIONS.map((section) => (
            <ContentCard
              key={section.type}
              title={section.title}
              description={section.description}
              icon={section.icon}
              to={`/sites/${siteId}/content/${section.path || section.type}`}
              singleton={section.singleton}
              complete={(counts[section.type] || 0) > 0}
            />
          ))}
        </div>
      </div>

      <div className="content-section">
        <h2 className="content-section-title">Sections</h2>
        <div className="content-cards-grid">
          {COLLECTION_SECTIONS.map((section) => (
            <ContentCard
              key={section.type}
              title={section.title}
              description={section.description}
              icon={section.icon}
              to={`/sites/${siteId}/content/${section.path || section.type}`}
              count={counts[section.type] || 0}
              singleton={section.singleton}
            />
          ))}
        </div>
      </div>

      <div className="content-section">
        <h2 className="content-section-title">Settings</h2>
        <div className="content-cards-grid">
          {CONFIG_SECTIONS.map((section) => (
            <ContentCard
              key={section.type}
              title={section.title}
              description={section.description}
              icon={section.icon}
              to={`/sites/${siteId}/content/${section.path || section.type}`}
              count={section.singleton ? undefined : (counts[section.type] || 0)}
              singleton={section.singleton}
              complete={section.singleton ? (counts[section.type] || 0) > 0 : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
